'use client'
import { type FC, memo, useMemo } from 'react'
import styled from 'styled-components'
import type { TypeCmsContentSlider } from '@/schema'
import { createUnderline, getP18, getP50, mq, vw } from '@/styles'
import { getHrefFromCMSUrl } from './ContentMedia'
import { Link } from '../Link'
import { SliderFlickity } from '../SliderFlickity'
import { useTranslator } from '@/hooks'
import { Img } from '../Img'

type Props = {
  data: TypeCmsContentSlider
}

type DivProps = {
  $col: TypeCmsContentSlider['col']
}

const getSlides = (slides:TypeCmsContentSlider['media'], viewMore = 'View more') => slides.map((item, idx) => {
  // slide_high
  const image = item?.medium?.image
  const size = image?.['slide_high']?.size

  if(item.link) {
    const href = getHrefFromCMSUrl(item.link)

    if (href?.[0] === '/') {
      return (
        <DivSlide key={idx} className='slide' $size={size}>
          <Link href={href}>
            {image && <Img className='media' media={image} />}
            <hgroup>
              {item.text && <h3>{item.text}</h3>}
              <button>{viewMore}</button>
            </hgroup>
          </Link>
        </DivSlide>
      )
    }

    return (
      <DivSlide key={idx} className='slide' $size={size}>
        <a href={href} target='_blank' rel='noreferrer'>
          {image && <Img className='media' media={image} />}
          <hgroup>
            {item.text && <h3>{item.text}</h3>}
            <button>{viewMore}</button>
          </hgroup>
        </a>
      </DivSlide>
    )
  }

  return (
    <DivSlide key={idx} className='slide' $size={size}>
      {image && <Img className='media' media={image} />}
      <hgroup>
        {item.text && <h3>{item.text}</h3>}
      </hgroup>
    </DivSlide>
  )
})

export const ContentSlider: FC<Props> = memo(({ data }) => {
  const { col, media } = data
  const { t } = useTranslator()
  const Slides = useMemo(() => getSlides(media, t('project.content_page.view_more') as string), [media, t])

  return (
    <Div className='ct ct--slider' $col={col}>
      <SliderFlickity options={{ cellAlign: 'right' }}>
        {Slides}
      </SliderFlickity>
    </Div>
  )
})

const Div = styled.div<DivProps>`
  height: fit-content;
  overflow: hidden;

  ${mq.greaterThan('tablet')} {
    grid-column: ${({ $col }) => `${$col.from} / span ${$col.expand}`};
  }

  .slider-wrapper {
    width: 100%;
  }
`

const DivSlide = styled.div<{ $size: TypeCmsContentSlider['media'][0]['size'] }>`
  display: grid;
  height: ${vw(266, 'mobile')};
  padding: 0 ${vw(5, 'mobile')};
  position: relative;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(266, 'tablet')};
    padding: 0 ${vw(5, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(530, 'desktop')};
    padding: 0 ${vw(12.5, 'desktop')};
    width: ${({ $size }) => ($size?.width && $size?.height) ? vw(530 * $size.width / $size.height, 'desktop') : 'fit-content'};
  }

  &:first-child {
    padding-left: ${vw(23, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding-left: ${vw(23, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-left: ${vw(270, 'desktop')};
    }
  }

  &:last-child {
    padding-right: ${vw(23, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding-right: ${vw(23, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding-right: ${vw(270, 'desktop')};
    }
  }

  a {
    align-items: center;
    display: grid;
    overflow: hidden;
    height: 100%;

    &:hover {
      /* button {
        background-size: 100% 1px; */
      //}

      .media {
        transform: scale(1.1);
      }
    }
  }

  .media,
  hgroup {
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;
  }

  .media {
    height: 100%;
    background-color: var(--color-img-placeholder);
    object-fit: cover;
    transition: 1000ms transform var(--transition-ease);
    width: ${({ $size }) => ($size?.width && $size?.height) ? vw(266 * $size.width / $size.height, 'mobile') : 'fit-content'};

    ${mq.greaterThan('nexus7')} {
      width: ${({ $size }) => ($size?.width && $size?.height) ? vw(266 * $size.width / $size.height, 'tablet') : 'fit-content'};
    }

    ${mq.greaterThan('tablet')} {
      width: ${({ $size }) => ($size?.width && $size?.height) ? vw(530 * $size.width / $size.height, 'desktop') : 'fit-content'};
    }
  }

  hgroup {
    align-items: center;
    color: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: ${vw(10, 'mobile')};
    justify-content: center;
    z-index: 2;

    ${mq.greaterThan('nexus7')} {
      gap: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      gap: ${vw(40, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      gap: 40px;
    }

    button {
      ${getP18()}
      ${createUnderline({ color: 'var(--color-white)', reverse: true })}
    }
  }

  h3 {
    ${getP50()}
    text-align: center;
    width: 100%;
  }
`
